import { UPDATE_MAIN_INFO_CASE_BULK } from 'src/redux/types'
import socketService from '../service/service'

const joinedSourceIds = new Set()

const normalizeId = (value) =>
  value === null || value === undefined || value === '' ? null : String(value)

const rejoinAllSources = (socket) => {
  if (!socket || !socket.connected) return
  joinedSourceIds.forEach((sourceId) => {
    socket.emit('joinMultiSourceRoom', { sourceId })
  })
}

/**
 * Join a multi-source room and set up listeners (CALL THIS WHEN YOU NEED TO LISTEN)
 */
export function setupMultiSourceListener(sourceId, dispatch) {
  const socket = socketService.getSocket()

  if (!socket) {
    console.error('[multiSource] Socket not available')
    return false
  }

  if (!socket.connected) {
    console.error('[multiSource] Socket not connected')
    return false
  }

  if (!sourceId) {
    console.error('[multiSource] sourceId is required')
    return false
  }

  const normalizedSourceId = normalizeId(sourceId)
  joinedSourceIds.add(normalizedSourceId)
  console.log(`[multiSource] Joining room for sourceId: ${normalizedSourceId}`)

  // Join the room FIRST, before setting up listeners
  socket.emit(
    'joinMultiSourceRoom',
    { sourceId: normalizedSourceId },
    (response) => {
      if (response?.success) {
        console.log(
          `[multiSource] Successfully joined room: ${normalizedSourceId}`,
        )
      } else {
        console.error(
          `[multiSource] Failed to join room: ${normalizedSourceId}`,
          response,
        )
      }
    },
  )

  // Now set up the listeners
  multiSourceListener(dispatch)

  return true
}

/**
 * Listen to multi-source events and dispatch Redux actions
 * NOTE: Call setupMultiSourceListener() instead of this function directly
 */
export function multiSourceListener(dispatch) {
  const socket = socketService.getSocket()
  if (!socket) return

  if (socket.__multiSourceListenerBound) return

  const handleExtractInfoUpdate = (payload) => {
    const { sourceId, extractInfo } = payload
    const normalizedSourceId = normalizeId(sourceId)

    if (joinedSourceIds.size > 0 && !joinedSourceIds.has(normalizedSourceId)) {
      return
    }
    if (!Array.isArray(extractInfo)) return

    dispatch({
      type: UPDATE_MAIN_INFO_CASE_BULK,
      payload: {
        sourceId: normalizedSourceId,
        updates: extractInfo.map((item) => ({
          sourceId: normalizedSourceId,
          caseInfoId: item.caseInfoId,
          fileInfoId: item.fileInfoId,
          fileName: item.fileName,
          caseIndex: item.caseIndex,
          status: item.status,
          extracted_text: item.extracted_text,
          is_valid: item.is_valid,
          confidenceScore: item.confidenceScore,
          validityReason: item.validityReason,
          needsReview: item.needsReview,
          main_info: item.main_info,
          isExtractFullcaseInfo: item.isExtractFullcaseInfo,
          full_info_status: item.full_info_status,
          error: item.error,
        })),
      },
    })
  }

  const handleExtractProgress = (payload) => {
    dispatch({
      type: 'UPDATE_FILE_PROGRESS',
      payload,
    })
  }

  socket.off('extractInfo_update', handleExtractInfoUpdate)
  socket.off('extract_progress', handleExtractProgress)
  socket.on('extractInfo_update', handleExtractInfoUpdate)
  socket.on('extract_progress', handleExtractProgress)
  socket.on('connect', () => rejoinAllSources(socket))
  socket.on('reconnect', () => rejoinAllSources(socket))

  socket.__multiSourceListenerBound = true
  socket.on('disconnect', () => {
    socket.__multiSourceListenerBound = false
  })
}

/**
 * Join a single source room manually (can be used later)
 */
export function joinMultiSourceRoomBySourceId(sourceId) {
  const socket = socketService.getSocket?.()
  if (!socket || !sourceId) return
  const normalizedSourceId = normalizeId(sourceId)
  // Track the id regardless of connection state -- handleExtractInfoUpdate
  // below drops any event for a sourceId not in this set, so if the socket
  // happened to be mid-reconnect right when a big ZIP upload finished (very
  // plausible: the upload itself can take long enough for the WS heartbeat
  // to drop), skipping this add here meant the id was never tracked and no
  // later event for this batch would ever get through -- rejoinAllSources()
  // on 'connect'/'reconnect' only re-emits ids already in the set, so an id
  // that was never added here never gets a second chance.
  joinedSourceIds.add(normalizedSourceId)
  console.log(`[multiSource] Joining room: ${normalizedSourceId} (connected=${socket.connected})`)
  if (socket.connected) {
    socket.emit('joinMultiSourceRoom', { sourceId: normalizedSourceId })
  }
  // If not connected yet, rejoinAllSources() (bound to 'connect'/'reconnect'
  // in multiSourceListener below) will emit the join as soon as the socket
  // comes up, since normalizedSourceId is now in joinedSourceIds.
}

/** Legacy / reserved — list fetch via socket (optional). */
export function getMultiSourceList(_dispatch, _user) {
  // Intentionally empty; batch list uses REST in MultiSourceList.
}

/**
 * Ask server to join the room used for file-extract events (alias of join).
 * Pass `sourceId` (batch / source id from multi-source upload).
 */
// export function getFileExtractedInfo(_dispatch, _user) {
//   // Room join is done explicitly via setupMultiSourceListener when a batch is active.
// }

/** Reserved for future case-status subscriptions. */
// export function updateMultiSourceCaseStatus(_dispatch, _user) {}
